// ============================================
// Path: /frontend/src/pages/ReceivedInquiriesPage.jsx
// Purpose: Shows inquiries RECEIVED about the seller's listings
// ============================================

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { inquiriesAPI } from '../api/inquiries.api';
import { useAuth } from '../hooks/useAuth';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Eye, Inbox, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <div className="space-y-3 sm:space-y-4">
    {Array.from({ length: 3 }).map((_, i) => (
      <Card key={i}>
        <CardContent className="p-3 sm:p-4 md:p-6">
          <Skeleton className="h-5 sm:h-6 w-3/4 mb-2" />
          <Skeleton className="h-3 sm:h-4 w-1/2 mb-3 sm:mb-4" />
          <Skeleton className="h-16 sm:h-20 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function ReceivedInquiriesPage() {
  console.log('🎨 [ReceivedInquiriesPage] Component mounted');

  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Reply states
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [replySuccess, setReplySuccess] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchReceivedInquiries = async () => {
      if (!user?.id) {
        console.log('⚠️ [ReceivedInquiriesPage] No user ID found');
        setLoading(false);
        return;
      }

      console.log('🔵 [ReceivedInquiriesPage] Fetching inquiries for user:', user.id);
      setLoading(true);

      try {
        // Use getUserInquiries which returns both sent and received
        const data = await inquiriesAPI.getUserInquiries(user.id);
        
        // We only want the received inquiries
        const receivedInquiries = data.received || [];

        console.log('✅ [ReceivedInquiriesPage] Received inquiries:', receivedInquiries.length);
        setInquiries(receivedInquiries);
        setError(null);
      } catch (err) {
        console.error('❌ [ReceivedInquiriesPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load received inquiries');
      } finally {
        setLoading(false);
      }
    };

    fetchReceivedInquiries();
  }, [user]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleViewDetails = (inquiry) => {
    console.log('🔵 [ReceivedInquiriesPage] Opening inquiry details:', inquiry.id);
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
    setReplyText(inquiry.sellerReply || ''); // Pre-fill if already replied
    setReplyError(null);
    setReplySuccess(false);
  };

  const handleCloseModal = () => {
    console.log('🔵 [ReceivedInquiriesPage] Closing inquiry details');
    setIsModalOpen(false);
    setSelectedInquiry(null);
    setReplyText('');
    setReplyError(null);
    setReplySuccess(false);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      setReplyError('Please enter a reply message');
      return;
    }

    if (!selectedInquiry) return;

    console.log('🔵 [ReceivedInquiriesPage] Sending reply to inquiry:', selectedInquiry.id);
    setIsReplying(true);
    setReplyError(null);
    setReplySuccess(false);

    try {
      const response = await inquiriesAPI.replyToInquiry(selectedInquiry.id, replyText);
      
      console.log('✅ [ReceivedInquiriesPage] Reply sent successfully');

      // Update the inquiry in the list
      setInquiries(prevInquiries =>
        prevInquiries.map(inq =>
          inq.id === selectedInquiry.id
            ? { ...inq, sellerReply: replyText, repliedAt: new Date().toISOString() }
            : inq
        )
      );

      // Update selected inquiry
      setSelectedInquiry(prev => ({
        ...prev,
        sellerReply: replyText,
        repliedAt: new Date().toISOString()
      }));

      // Show success
      setReplySuccess(true);

      // Optionally close modal after a delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
    } catch (err) {
      console.error('❌ [ReceivedInquiriesPage] Reply error:', err);
      setReplyError(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setIsReplying(false);
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-headline font-bold mb-1 sm:mb-2">
              Received Inquiries
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
              Inquiries from interested buyers about your listings
            </p>
          </div>

          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <Card>
              <CardContent className="p-4 sm:p-6">
                <p className="text-xs sm:text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          ) : inquiries.length === 0 ? (
            <Card>
              <CardContent className="p-6 sm:p-8 md:p-12 text-center">
                <Inbox className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-muted-foreground" />
                <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-1 sm:mb-2">No Inquiries Yet</h3>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground mb-4 sm:mb-6">
                  You haven't received any inquiries about your listings yet.
                </p>
                <Link to="/my-listings">
                  <Button className="text-xs sm:text-sm h-9 sm:h-10">View My Listings</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 sm:p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-3 sm:mb-4">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mb-1">
                          <h3 className="text-sm sm:text-base md:text-lg font-semibold">
                            {inquiry.listing?.title || inquiry.listingTitle || 'Listing'}
                          </h3>
                          {inquiry.sellerReply ? (
                            <Badge variant="default" className="bg-green-600 w-fit text-[10px] sm:text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Replied
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="w-fit text-[10px] sm:text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">
                          Received on {inquiry.createdAt ? format(new Date(inquiry.createdAt), 'PPP') : 'N/A'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleViewDetails(inquiry)}
                          className="text-xs sm:text-sm h-8 sm:h-9"
                        >
                          <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          {inquiry.sellerReply ? 'View Conversation' : 'Reply'}
                        </Button>
                        <Link to={`/listing/${inquiry.listingId}`}>
                          <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9">
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            View Listing
                          </Button>
                        </Link>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-2.5 sm:p-3 md:p-4 rounded-md mb-3 sm:mb-4">
                      <p className="text-xs sm:text-sm line-clamp-2">
                        {inquiry.message || 'No message'}
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 text-[10px] sm:text-xs md:text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{inquiry.inquirer?.email || inquiry.inquirerEmail}</span>
                      </div>
                      {inquiry.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>{inquiry.phone}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* INQUIRY DETAILS MODAL */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="space-y-1.5 sm:space-y-2">
            <DialogTitle className="text-base sm:text-lg md:text-xl">Inquiry Details</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Contact information and message from potential buyer
            </DialogDescription>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">About Listing</h4>
                <p className="text-sm sm:text-base font-semibold">
                  {selectedInquiry.listing?.title || selectedInquiry.listingTitle || 'Listing'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1 flex items-center gap-2">
                    <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                    Buyer's Email
                  </h4>
                  <a 
                    href={`mailto:${selectedInquiry.inquirer?.email || selectedInquiry.inquirerEmail}`}
                    className="text-xs sm:text-sm md:text-base text-primary hover:underline truncate block"
                  >
                    {selectedInquiry.inquirer?.email || selectedInquiry.inquirerEmail}
                  </a>
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1 flex items-center gap-2">
                    <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                    Buyer's Phone
                  </h4>
                  {selectedInquiry.phone ? (
                    <a 
                      href={`tel:${selectedInquiry.phone}`}
                      className="text-xs sm:text-sm md:text-base text-primary hover:underline"
                    >
                      {selectedInquiry.phone}
                    </a>
                  ) : (
                    <p className="text-xs sm:text-sm md:text-base text-muted-foreground">Not provided</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Date Received</h4>
                <p className="text-xs sm:text-sm md:text-base">
                  {selectedInquiry.createdAt ? format(new Date(selectedInquiry.createdAt), 'PPP p') : 'N/A'}
                </p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Buyer's Message</h4>
                <div className="bg-muted p-3 sm:p-4 rounded-md">
                  <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap">
                    {selectedInquiry.message || 'No message provided'}
                  </p>
                </div>
              </div>

              {/* SELLER REPLY SECTION */}
              <div className="border-t pt-3 sm:pt-4">
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Your Reply</h4>
                
                {selectedInquiry.sellerReply && selectedInquiry.repliedAt && (
                  <div className="mb-3 sm:mb-4">
                    <div className="bg-green-50 border border-green-200 p-3 sm:p-4 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        <span className="text-[10px] sm:text-xs md:text-sm text-green-700 font-medium">
                          Replied on {format(new Date(selectedInquiry.repliedAt), 'PPP p')}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap text-gray-700">
                        {selectedInquiry.sellerReply}
                      </p>
                    </div>
                  </div>
                )}

                <Textarea
                  placeholder={selectedInquiry.sellerReply ? "Update your reply..." : "Type your reply to the buyer..."}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="min-h-[100px] sm:min-h-[120px] mb-2 text-xs sm:text-sm"
                  disabled={isReplying}
                />

                {replyError && (
                  <p className="text-xs sm:text-sm text-destructive mb-2">{replyError}</p>
                )}

                {replySuccess && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-green-600 mb-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Reply sent successfully!</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a href={`mailto:${selectedInquiry.inquirer?.email || selectedInquiry.inquirerEmail}`} className="w-full sm:w-auto">
                      <Button variant="outline" size="sm" className="w-full text-xs sm:text-sm h-9 sm:h-10">
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                        Email
                      </Button>
                    </a>
                    {selectedInquiry.phone && (
                      <a href={`tel:${selectedInquiry.phone}`} className="w-full sm:w-auto">
                        <Button variant="outline" size="sm" className="w-full text-xs sm:text-sm h-9 sm:h-10">
                          <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                          Call
                        </Button>
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                      disabled={isReplying}
                      className="flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleSendReply}
                      disabled={isReplying || !replyText.trim()}
                      className="flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
                    >
                      {isReplying ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                          {selectedInquiry.sellerReply ? 'Update Reply' : 'Send Reply'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}